import { Box, Button, useTheme } from '@mui/material';
import {
  type ChatPartRendererProps,
  ToolPart,
  useMessageContentTabIndex,
} from '@mui/x-chat/headless';

import { AgenticChatActivityDisclosure } from './Disclosure';
import { AgenticChatReasoningPreview } from './ReasoningPreview';
import { isAgenticChatActivityActive } from './isActive';
import {
  type AgenticChatActivityMessagePart,
  isAgenticChatActivityPartVisible,
} from './isVisiblePart';

type AgenticChatActivityPartProps = ChatPartRendererProps<AgenticChatActivityMessagePart> & {
  reasoningPresentation?: 'LivePreview' | 'History';
  onFocusChange?: (focus: 'Focused' | 'Blurred') => void;
};

export function AgenticChatActivityPart(props: AgenticChatActivityPartProps) {
  const { part, message, index, onToolCall, reasoningPresentation, onFocusChange } = props;
  const theme = useTheme();
  const tabIndex = useMessageContentTabIndex();
  const isActive = isAgenticChatActivityActive({ part, message, index });
  const isReasoningActive = part.type === 'reasoning' && isActive;

  if (!isAgenticChatActivityPartVisible({ part, message, index })) {
    return null;
  }

  return (
    <AgenticChatActivityDisclosure
      onFocusChange={(focused) => onFocusChange?.(focused)}
      defaultExpansion={
        isReasoningActive || reasoningPresentation === 'LivePreview' ? 'Expanded' : 'Collapsed'
      }
      icon={part.type === 'reasoning' ? 'Visible' : 'Hidden'}
      label={
        part.type === 'reasoning'
          ? 'Reasoning'
          : (part.toolInvocation.title ?? part.toolInvocation.toolName.replaceAll('-', ' '))
      }
      errorCount={
        part.type !== 'reasoning' &&
        ['output-error', 'output-denied'].includes(part.toolInvocation.state)
          ? 1
          : 0
      }
      animation={isActive ? 'Active' : 'Inactive'}
    >
      <Box
        sx={{
          paddingLeft: part.type === 'reasoning' ? 1 : undefined,
          overflowWrap: 'anywhere',
          whiteSpace: 'pre-wrap',
          '& pre': { whiteSpace: 'pre-wrap', typography: 'monospace' },
        }}
      >
        {part.type === 'reasoning' ? (
          <AgenticChatReasoningPreview text={part.text} presentation={reasoningPresentation} />
        ) : (
          <ToolPart
            part={part}
            message={message}
            index={index}
            onToolCall={(payload) => onToolCall?.(payload)}
            slots={{ approveButton: Button, denyButton: Button }}
            slotProps={{
              header: { style: { display: 'none' } },
              sectionContent: (ownerState) => ({
                tabIndex,
                role: 'region',
                'aria-label': `${ownerState.toolName} ${ownerState.section}`,
                style: { maxHeight: '15lh', overflow: 'auto' },
              }),
              error: { role: 'alert', style: { color: theme.palette.error.main } },
            }}
          />
        )}
      </Box>
    </AgenticChatActivityDisclosure>
  );
}
